import axios, { AxiosError } from "axios"; // tslint:disable-line match-default-export-name

import { gGateway } from "@utils/gateway";

import { api } from "@courses-models/progression/src/ts/config";

/**
 * Specification of the response format when fetching progression objects from
 * the remote endpoint.
 */
type ProgressionResponse = {
    objectID: string;
    modified: number;
    lastLocation: string;
    lastLocationTitle: string;
    furthestLocation: string;
    furthestLocationTitle: string;
    completed: boolean;
    completion: number;
};

/**
 * Specifies the format of the data, which is used to describe a single learning
 * object that a progression object is associated with.
 */
type LearningObjectData = {
    /**
     * Contains the unique id of the learning object.
     */
    id: string;

    /**
     * Contains the title of the learning object, which is useful when rendering
     * lists of progressions.
     */
    title: string;

    /**
     * Specifies the type of the learning object, which is useful when
     * attempting to open the progression in new windows.
     */
    type: string; // tslint:disable-line no-reserved-keywords
};

/**
 * Specifies the data structure for a progression object as stored in the
 * UserData api.
 */
type ProgressionData = {
    /**
     * Will contain the id of the progression object stored in the UserData
     * backend, so we can properly update it when data is being changed.
     */
    id?: string;

    /**
     * Contains the timestamp for when the progression object was last modified
     * (ie. when the user was last navigating the product).
     */
    modifiedAt: number;

    /**
     * Contains a handle that the client can use to navigate to the last known
     * location within the product (this can be anything, for example an
     * internal path or an id of an element).
     */
    lastLocation: string;

    /**
     * Contains the title of the last known location within the product, so we
     * can provide the user with a meaningful message of where he'll be brought
     * to.
     */
    lastLocationTitle: string;

    /**
     * Contains a handle that the client can use to navigate to the furthest
     * known location within the product (this can be anything, for example an
     * internal path or an id of an element).
     */
    furthestLocation: string;

    /**
     * Contains the title of the furthest known location within the product, so
     * we can provide the user with a meaningful message of where he'll be
     * brought to.
     */
    furthestLocationTitle: string;

    /**
     * Specifies if the user has fully completed the content of the product yet.
     */
    completed: boolean;

    /**
     * Specifies the completion rate of the product in percent (ie. on a scale
     * from 0 to 100).
     */
    completion: number;
};

/**
 * Implementation of progression objects, which allows clients to easily track
 * the user's current progression.
 */
export class ProgressionModel {
    /**
     * Contains the id of the user who owns this progression object.
     */
    public readonly userId: string;

    /**
     * Contains the ID of the institution that the user is associated with.
     */
    public readonly userInstitutionId: string;

    /**
     * Contains the id of the product, which the learning object has been
     * created for.
     */
    public readonly productId: string;

    /**
     * Contains data about the learning object, that this progression object is
     * associated with.
     */
    public readonly learningObject: LearningObjectData;

    /**
     * Contains the progression object
     */
    protected progression?: ProgressionData;

    /**
     * Apply data initially when creating a progression model.
     */
    constructor(initial: {
        userId: string;
        userInstitutionId: string;
        productId: string;
        learningObject: LearningObjectData;
        progression?: ProgressionData;
    }) {
        this.userId = initial.userId;
        this.userInstitutionId = initial.userInstitutionId;
        this.productId = initial.productId;
        this.learningObject = initial.learningObject;
        this.progression = initial.progression;
    }

    /**
     * Specifies if the user has reached the final point of the content (ie. if
     * he has completed the learning object).
     */
    public get isCompleted(): boolean {
        // Bail out if no progression exists just yet
        if (!this.progression) {
            return false;
        }

        return this.progression.completed;
    }

    /**
     * Specifies how far along the user is within the learning object in
     * percent (ie. how close he is to completing the learning object on a scale
     * from 0-100).
     */
    public get completion(): number {
        // Bail out if no progression exists just yet
        if (!this.progression) {
            return 0;
        }

        return this.progression.completion;
    }

    /**
     * Specifies the last known location of the user within the learning object,
     * so we can easily bring him back there.
     */
    public get lastLocation(): { src: string; title: string } | undefined {
        // Bail out if no progression exists just yet
        if (!this.progression) {
            return undefined;
        }

        return {
            src: this.progression.lastLocation,
            title: this.progression.lastLocationTitle,
        };
    }

    /**
     * Specifies the furthest known location, that the user has reached within
     * the learning object, so we can easily bring him back there.
     */
    public get furthestLocation(): { src: string; title: string } | undefined {
        // Bail out if no progression exists just yet
        if (!this.progression) {
            return undefined;
        }

        return {
            src: this.progression.furthestLocation,
            title: this.progression.furthestLocationTitle,
        };
    }

    /**
     * Contains an unix timestamp for when the progression object was last being
     * updated.
     */
    public get lastUpdated(): number {
        // Bail out if no progression exists just yet
        if (!this.progression) {
            return -1;
        }

        return this.progression.modifiedAt;
    }

    /**
     * Helper that'll fetch the progression object for the user, which has been
     * associated with the given product.
     */
    public static async GET_BY_LEARNING_OBJECT({ userId, userInstitutionId, productId, learningObject }: {
        userId: string;
        userInstitutionId: string;
        productId: string;
        learningObject: LearningObjectData;
    }): Promise<ProgressionModel> {
        const url = (
            `${api.userData}/api/Progressions` +
            `?userID=${encodeURIComponent(userId)}` +
            `&learningObjectId=${encodeURIComponent(learningObject.id)}`
        );

        try {
            const response = await axios.get<ProgressionResponse>(gGateway(url));
            const data = response.data;

            if (response.status === 204) {
                // If we get here, a progression object simply does not exist for
                // this user / learning object combo yet - return an empty object
                return new ProgressionModel({
                    userId,
                    userInstitutionId,
                    productId,
                    learningObject,
                });
            }

            return new ProgressionModel({
                userId,
                userInstitutionId,
                productId,
                learningObject,
                progression: {
                    id: data.objectID,
                    modifiedAt: data.modified,
                    lastLocation: data.lastLocation,
                    lastLocationTitle: data.lastLocationTitle,
                    furthestLocation: data.furthestLocation,
                    furthestLocationTitle: data.furthestLocationTitle,
                    completed: data.completed,
                    completion: data.completion,
                },
            });
        } catch (err) {
            const typedErr = <unknown>err;

            // Propagate anything but error 404 directly
            if (!isAxiosError(typedErr) || !typedErr.response || typedErr.response.status !== 404) {
                throw err;
            }

            // If we get here, a progression object simply does not exist for
            // this user / learning object combo yet - return an empty object
            return new ProgressionModel({
                userId,
                userInstitutionId,
                productId,
                learningObject,
            });
        }
    }

    /**
     * Helper that'll update the progression status, making sure that it will
     * be updated in the UserData backend as well.
     */
    public async update(data: {
        src: string;
        title: string;
        completion: number;
    }): Promise<void> {
        if (!this.progression) {
            // If the progression object doesn't exist yet, then create it now
            this.progression = {
                modifiedAt: Math.round(Date.now() / 1000),
                lastLocation: data.src,
                lastLocationTitle: data.title,
                furthestLocation: data.src,
                furthestLocationTitle: data.title,
                completed: data.completion >= 100,
                completion: Math.max(0, Math.min(100, data.completion)),
            };
        } else {
            // Silently bail out, if nothing has changed
            if (
                this.progression.lastLocation === data.src &&
                this.progression.lastLocationTitle === data.title
            ) {
                return;
            }

            // ... Otherwise update the last known location
            this.progression.lastLocation = data.src;
            this.progression.lastLocationTitle = data.title;

            // Determine if the furthest location has been changed?
            if (data.completion >= this.progression.completion) {
                this.progression.furthestLocation = data.src;
                this.progression.furthestLocationTitle = data.title;

                // Also update completion status
                this.progression.completed = data.completion >= 100;
                this.progression.completion = Math.max(0, Math.min(100, data.completion));
            }
        }

        // Prepare the request body which we need to update the remote object
        const requestBody = {
            userID: this.userId,
            userInstitutionID: this.userInstitutionId,
            productID: this.productId,
            learningObject: this.learningObject,
            lastLocation: this.progression.lastLocation,
            lastLocationTitle: this.progression.lastLocationTitle,
            furthestLocation: this.progression.furthestLocation,
            furthestLocationTitle: this.progression.furthestLocationTitle,
            completed: this.progression.completed,
            completion: this.progression.completion,
        };

        // Determine if we need to create a new object, or if we're going to
        // update an existing one
        await axios({
            method: this.progression.id ? "PUT" : "POST",
            url: gGateway(`${api.userData}/api/Progressions`),
            data: JSON.stringify(requestBody),
            headers: {
                "Content-Type": "application/json; charset=utf-8",
            },
        });
    }
}

/**
 * Typeguard that verifies if the error object is an AxiosError object.
 */
function isAxiosError(err: unknown): err is AxiosError {
    return err instanceof Error && "config" in err;
}
